/**
 * The quote's adapter — `builder-engine-and-consistency.md` §5.2.
 *
 * Where the quote builder's money comes from (the server's recalc, re-read
 * whole after every mutation), how a visit starts (a calendar date), what the
 * quantity cell is (a number) and which toolbar it carries. Everything the page
 * held besides its markup moved here at step 8, verbatim: the one piece of
 * server state, every mutation, the editor that is open and what each click
 * opens. The page fetches nothing and decides nothing; it draws what this
 * hands it.
 *
 * The package builder's adapter (money from the server's `/preview`, starts
 * relative, qty a rule) and the service recipe's arrive with the pages that
 * use them, at steps 10 and 9.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import type {
  MarginScope, MeResponse, ProposalSelection, ProposalView,
  QuoteCheck, QuoteLineKind, QuoteTree, QuoteVisitView,
} from '@rowted/shared'
import { request } from '../../lib/api.js'
import { usePhone, useNarrow } from '../../lib/use-narrow.js'
import type { MenuItem } from '../../components/context-menu.js'
import type { Pickable } from '../../quote/quote-editors.js'
import { OPTIONAL_LABEL, OPTIONAL_SUB, ROW_MENU, VISIT_MENU } from '../copy.js'
import type { BuilderAdapter } from '../model.js'

export type Tab = 'build' | 'billing' | 'proposal' | 'activity'

/** What the quote builder is, against the things a page may differ in (§5.2). */
export const QUOTE_ADAPTER: BuilderAdapter = {
  qty: 'number',
  money: 'recalc',
  starts: 'date',
  toolbar: ['search', 'addVisit', 'addPackage', 'measure', 'pricesAndCosts', 'collapseAll'],
}


/**
 * Which filter chip a "+ Kind" link opens on.
 *
 * Only four: a sub is labor, and the picker stopped offering it as a sixth
 * kind that is the fifth one under another name.
 */
/**
 * Which chip a "+ Material" link opens the picker on.
 *
 * Almost the identity, because the picker's chips *are* the line kinds now —
 * except `sub`, which has no catalog behind it. "+ Sub" opens the picker with
 * nothing preselected rather than on a chip that would show an empty list.
 */
export type PickChip = 'item' | 'labor' | 'equipment' | 'deployment'
export const PICK_CHIP = (kind: QuoteLineKind): PickChip | undefined =>
  (kind === 'sub' ? undefined : kind)


/** The one editor that is open, named by what it is editing. */
export type Editing =
  | { kind: 'line'; lineId: string; serviceId: string }
  | { kind: 'visit'; visitId: string; section?: 'name' | 'schedule' | 'billing' | 'notes' | 'choices' }
  /** `focus` is the ⋮'s Rename (item 4): the popover opens with its Name in hand. */
  | { kind: 'service'; serviceId: string; focus?: 'name' }
  /** cb-19 · 1: the measure chip's popover. */
  | { kind: 'measure'; serviceId: string }
  /** The same popover off a package group's chip (Aaron, review item 4.1). */
  | { kind: 'packageMeasure'; instanceId: string }
  /**
   * `startKind` opens the picker on a chip; `visitId` means a whole service.
   * `afterLineId` / `afterServiceId` are a ⋮'s Add … below (item 4): the
   * pick lands directly under that row rather than at the foot of the list.
   */
  | { kind: 'add'; startKind?: PickChip; afterLineId?: string }
  | { kind: 'addService'; visitId: string; afterServiceId?: string }
  /**
   * Add visit ▾ → From a service… (Aaron, item 3.2): the service picker, and
   * the pick lands on a visit of its own rather than the one that is open.
   */
  | { kind: 'addServiceVisit' }
  | { kind: 'package'; instanceId: string }
  | { kind: 'addPackage' }
  /** Board 10's Add visit menu (visits §5.1.1). */
  | { kind: 'addVisit' }
  /** **On the proposal** for one visit (`docs/proposal.md` §10.5). */
  | { kind: 'proposal'; visitId: string }
  /** One visit's margin popover — one at a time, like every editor (folder 44 §7.3). */
  | { kind: 'margin'; visitId: string }
  | null

export type QuoteBuilder = ReturnType<typeof useQuoteBuilder>

export function useQuoteBuilder(me: MeResponse) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const phone = usePhone()
  const narrow = useNarrow(1180)

  const [quote, setQuote] = useState<QuoteTree | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /* Who Send emailed the proposal to, or why nobody (`docs/proposal.md` §10.2). */
  /* Any state change's white toast (folder 52; findings §11) — Send's, and the stage actions'. */
  const [sentNote, setSentNote] = useState<{ tone: 'default'; text: string } | null>(null)

  /*
   * Which card has the ring, which visits and services are folded up, and
   * whether the totals rail is on screen. All three are about *looking* at the quote
   * rather than about what it says, so none of them is stored: a collapsed
   * service is not a fact about the quote, it is a fact about this minute.
   */
  const [selected, setSelected] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  /*
   * The rail, on or off — "Prices & costs" in the toolbar. There used to be a
   * Customer view here that hid costs and margins, which was not the
   * customer's view of anything (the Proposal tab is that), so the one
   * switch left is whether the totals column is on screen.
   */
  const [railOn, setRailOn] = useState(true)
  const [menu, setMenu] = useState<{ items: MenuItem[]; x: number; y: number } | null>(null)

  /*
   * One editor at a time, named by what it is editing. A record of four
   * booleans would let two of them be open at once, which on a popover means
   * two boxes hanging off the same row.
   */
  const [editing, setEditing] = useState<Editing>(null)
  /*
   * The rail's Base · Options · All, held here so a visit popover's "Open
   * Margin ›" can scroll to the card and set it. Resets on reload (§6.4).
   */
  const [marginScope, setMarginScope] = useState<MarginScope>('base')
  /** The phone and iPad summary sheet (qb-08 C, qb-09 · 3). */
  const [summaryOpen, setSummaryOpen] = useState(false)
  const anchor = useRef<HTMLElement | null>(null)
  const searchBox = useRef<HTMLButtonElement | null>(null)
  const packageBox = useRef<HTMLSpanElement | null>(null)
  /** Board 10's `Add visit ▾` — the menu hangs off it. */
  const addVisitBox = useRef<HTMLSpanElement | null>(null)
  /** The tree the last write answered with, for a caller that needs a row it just made. */
  const lastTree = useRef<QuoteTree | null>(null)

  /**
   * Which service a search result lands in.
   *
   * The last one somebody touched — opened, added to, edited a line in. It is
   * what "the open service" means in the footer hint, and it is why ⏎ does the
   * obvious thing nine times in ten without anybody choosing a target.
   */
  const [openService, setOpenService] = useState<string | null>(null)
  /**
   * The customer's own document, and what the Proposal tab has ticked.
   *
   * The tab used to render the tree it already had, and the costs and margins
   * in it went to the browser on every quote (`docs/proposal.md` §1.1). It
   * reads the same projection the portal does now, so the preview is not a
   * second rendering of anything — it is the document.
   */
  const [proposal, setProposal] = useState<ProposalView | null>(null)
  /*
   * Its own error, not the builder's.
   *
   * `error` is what a *mutation* said, and it is drawn inside the document —
   * so a read that failed with nothing to draw it in would have left the tab
   * saying "Loading the proposal…" for ever, which is a spinner standing in
   * for a message nobody wrote.
   */
  const [proposalError, setProposalError] = useState<string | null>(null)
  const [selection, setSelection] = useState<ProposalSelection | null>(null)
  const [divisions, setDivisions] = useState<{ id: string; name: string }[]>([])

  useEffect(() => {
    void request<{ items: { id: string; name: string }[] }>('/catalog-admin/divisions')
      .then((body) => setDivisions(body.items))
      .catch(() => setDivisions([]))
  }, [])

  const tab = (params.get('tab') as Tab | null) ?? 'build'
  /*
   * §7: a phone has two screens, and which one it is on is in the URL. Back
   * is then the browser's back, a visit can be linked to, and reloading does
   * not throw somebody out to the top of the quote.
   */
  const openVisitId = params.get('visit')
  const openPhoneVisit = (visitId: string | null) => {
    setParams((was) => {
      const next = new URLSearchParams(was)
      if (visitId) next.set('visit', visitId)
      else next.delete('visit')
      return next
    })
  }
  const openTab = (next: Tab) => {
    const copy = new URLSearchParams(params)
    copy.set('tab', next)
    setParams(copy, { replace: true })
  }

  /*
   * Only a draft is ever edited (walkthrough findings §4, Oct 30): a sent,
   * viewed, approved or closed quote renders its tree read-only — no live
   * inputs, no add buttons, no row ⋯ — rather than letting the server refuse
   * the write afterwards. And a draft of a cancelled job is read-only while
   * the job is cancelled (`docs/job-stages.md` §5, Cancel job), which is
   * `editable()`'s own rule on the server.
   */
  /* `quotes: edit` whatever the quote's state — what the customer sent is marked handled after it went out. */
  const canHandle = me.permissions.quotes === 'edit' || me.permissions.quotes === 'full'
  const canEdit = canHandle
    && quote?.status === 'draft'
    && quote.closed?.job.status !== 'cancelled'

  const load = useCallback(async () => {
    try {
      const body = await request<{ quote: QuoteTree }>(`/quotes/${id}/tree`)
      setQuote(body.quote)
      setError(null)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That quote could not be loaded.')
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  /**
   * Every mutation, in one place.
   *
   * The response *is* the new state, so there is no reconciliation step and no
   * optimistic update to roll back. An error leaves the tree exactly as it
   * was, which is the honest thing to show: the edit did not happen.
   */
  const mutate = useCallback(async (
    path: string,
    init: { method: 'POST' | 'PATCH' | 'PUT' | 'DELETE'; body?: unknown },
  ) => {
    setBusy(true)
    try {
      const body = await request<{
        quote?: QuoteTree; emailedTo?: { name: string; email: string }[]; notEmailed?: string | null
      }>(`/quotes/${id}${path}`, init)
      if (path === '/send' && body.emailedTo) setSentNote(sendNote(body.emailedTo, body.notEmailed ?? null))
      /*
       * Every builder endpoint answers with the whole recomputed tree, so the
       * response *is* the new state. Send and approve are the two that do not
       * — they answer with what they created — so those re-read. Without it
       * the page went on showing "Sent" and a live Approve button over a
       * quote that had just been approved.
       */
      /*
       * Never an older tree over a newer one (findings Oct 30 §2). Two writes
       * in flight — a quantity, Tab, the next one — answer in the order they
       * committed, and `version` goes up with every commit, so an answer
       * that lands behind a later one is dropped rather than drawn: the rows
       * would otherwise show the first write's totals until the next action.
       */
      if (body.quote) {
        const tree = body.quote
        const newer = (was: QuoteTree | null) =>
          !was || was.id !== tree.id || tree.version >= was.version
        setQuote((was) => (newer(was) ? tree : was))
        if (newer(lastTree.current)) lastTree.current = tree
      } else await load()
      setError(null)
      return true
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not save.')
      return false
    } finally {
      setBusy(false)
    }
  }, [id, load])

  /*
   * A choice made in the visit editor's Choices field (§8.4): written now, so
   * a second visit's field lists it, and found again in the answer by name —
   * the server finds-or-creates on the name, so this is the row it made or
   * the one that already had it.
   */
  const createChoice = useCallback(async (
    name: string, groupId?: string, newQuestion?: boolean,
  ): Promise<{ id: string; groupId: string } | null> => {
    try {
      const body = await request<{ quote: QuoteTree }>(`/quotes/${id}/options`, {
        method: 'POST', body: { name, groupId, newQuestion: newQuestion || undefined },
      })
      setQuote(body.quote)
      setError(null)
      const wanted = name.trim().toLowerCase()
      const made = body.quote.chooseOptions
        .filter((option) => option.name.toLowerCase() === wanted
          && (!groupId || option.groupId === groupId))
        .at(newQuestion ? -1 : 0)
      return made ? { id: made.id, groupId: made.groupId } : null
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That did not save.')
      return null
    }
  }, [id])

  /*
   * The proposal, re-read whenever the quote changes.
   *
   * It is a server projection, not a view of the tree the page already holds,
   * so it cannot be derived locally — which is the point (`docs/proposal.md`
   * §2 rule 1). Keyed on `quote.version` rather than on the object, because
   * every mutation hands back a new tree and the document only changes when
   * the quote does. Only while the tab is open: an estimator who never opens
   * Proposal pays nothing for it.
   */
  useEffect(() => {
    if (tab !== 'proposal') return undefined
    let live = true
    void request<{ proposal: ProposalView }>(`/quotes/${id}/proposal`)
      .then((body) => {
        if (!live) return
        setProposal(body.proposal)
        setProposalError(null)
      })
      .catch((cause: unknown) => {
        if (!live) return
        setProposalError(cause instanceof Error
          ? cause.message
          : 'The proposal could not be loaded.')
      })
    return () => { live = false }
  }, [id, tab, quote?.version])

  /*
   * ⌘/ rather than ⌘K: ⌘K is the app's own search and a quote builder that
   * stole it would break the one shortcut everybody already knows.
   */
  /* ⌘/ opens the add search only where the search is drawn — a draft (findings §4). */
  const searchable = useRef(false)
  searchable.current = canEdit
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === '/' && searchable.current) {
        event.preventDefault()
        anchor.current = searchBox.current
        setEditing({ kind: 'add' })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const toggle = (key: string) => setCollapsed((was) => {
    const next = new Set(was)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    return next
  })

  /*
   * Every box that folds: the visits and their services, and — since step 21
   * item 4.3 — the choice boxes and the packages, so Collapse all folds the
   * whole Build tab and unfolding it opens all of them again.
   */
  const everything = [
    ...(quote?.chooseGroups ?? []).map((group) => group.id),
    ...(quote?.packages ?? []).map((instance) => instance.id),
    ...(quote?.visits ?? []).flatMap(
      (visit) => [visit.id, ...visit.services.map((service) => service.id)]),
  ]
  const allCollapsed = everything.length > 0 && everything.every((key) => collapsed.has(key))
  const collapseAll = () => setCollapsed(allCollapsed ? new Set() : new Set(everything))

  /** Jumping to whatever a Before-you-send row is about. */
  const goToCheck = (check: QuoteCheck) => {
    if (check.visitId) {
      setSelected(check.visitId)
      setCollapsed((was) => {
        const next = new Set(was)
        next.delete(check.visitId!)
        if (check.serviceId) next.delete(check.serviceId)
        return next
      })
      document.getElementById(`visit-${check.visitId}`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
    /*
     * A row that names one service opens that service's editor, because the
     * thing it is complaining about — a missing measure, missing hours — is a
     * field in it. Scrolling to the right card and stopping there makes the
     * person hunt for the control the row already knew about.
     */
    if (check.serviceId) openServiceEditor(check.serviceId)()
    /*
     * An ordering problem (§5.4) is fixed in the visit's When, so `Fix ›`
     * opens its popover at the schedule, hung off the card it scrolled to.
     */
    if (check.fix && check.visitId) {
      anchor.current = document.getElementById(`visit-${check.visitId}`)
      openVisit(check.visitId, 'schedule')()
    }
    if (check.key === 'plans' || check.key === 'empty-plans' || check.key === 'milestones') {
      openTab('billing')
    }
  }

  const blocking = useMemo(
    () => (quote?.checks ?? []).filter((check) => check.level === 'blocking'),
    [quote])
  /** Whether any line is a sub — the margin footnote's second sentence (§6.5). */
  const hasSubs = useMemo(
    () => (quote?.visits ?? []).some((visit) => visit.services.some(
      (service) => service.lines.some((line) => line.kind === 'sub'))),
    [quote])

  /* Below 1180 the rail has no room, and the toolbar can switch it off. */
  const railHidden = !railOn || narrow

  return {
    id, navigate, params, setParams, phone, narrow, quote, setQuote,
    error, setError, busy, setBusy, sentNote, setSentNote, selected, setSelected, collapsed, setCollapsed,
    railOn, setRailOn, menu, setMenu, editing, setEditing, marginScope, setMarginScope,
    summaryOpen, setSummaryOpen, anchor, searchBox, packageBox, addVisitBox, openService, setOpenService, proposal,
    setProposal, proposalError, setProposalError, selection, setSelection, divisions, tab, openVisitId,
    openPhoneVisit, openTab, canEdit, canHandle, load, mutate, createChoice, toggle, everything,
    allCollapsed, collapseAll, goToCheck, blocking, hasSubs, railHidden, at, openVisit,
    openServiceEditor, openMeasure, openPackageMeasure, openMargin, fixCosts, openLine, openAdd,
    addPackage, addService, add,
    visitMenu, serviceMenu, lineMenu, startVisit, duplicateVisit, addServiceVisit,
  }

  /**
   * Opening an editor remembers what was clicked, so the popover hangs off it.
   *
   * A ref rather than state: the anchor is read once, at the moment the
   * popover measures itself, and putting it in state would re-render the whole
   * tree to move a box six pixels.
   */
  function at<T extends HTMLElement>(event: { currentTarget: T }) {
    anchor.current = event.currentTarget
  }

  /*
   * Declarations, not `const` arrows: everything below the component's
   * `return` only works because function declarations hoist. A `const` here
   * is in its temporal dead zone while the JSX above is being built, and the
   * page renders blank with a ReferenceError.
   */
  function openVisit(visitId: string, section: 'name' | 'schedule' | 'billing' | 'notes' | 'choices') {
    return (event?: unknown) => {
      if (event && typeof event === 'object' && 'currentTarget' in event) {
        at(event as { currentTarget: HTMLElement })
      }
      setSelected(visitId)
      setEditing({ kind: 'visit', visitId, section })
    }
  }

  function openServiceEditor(serviceId: string) {
    return (event?: unknown) => {
      if (event && typeof event === 'object' && 'currentTarget' in event) {
        at(event as { currentTarget: HTMLElement })
      }
      setOpenService(serviceId)
      setEditing({ kind: 'service', serviceId })
    }
  }

  /** The measure chip opens the Measure popover (§6.7 item 4), never the service's own. */
  function openMeasure(serviceId: string) {
    return (event?: unknown) => {
      if (event && typeof event === 'object' && 'currentTarget' in event) {
        at(event as { currentTarget: HTMLElement })
      }
      setEditing({ kind: 'measure', serviceId })
    }
  }

  /**
   * A package group's measure chip opens the same popover, about the package
   * (Aaron, review item 4.1): one number re-scales every visit under it, so
   * it is looked up and linked exactly as a service's is.
   */
  function openPackageMeasure(instanceId: string) {
    return (event?: unknown) => {
      if (event && typeof event === 'object' && 'currentTarget' in event) {
        at(event as { currentTarget: HTMLElement })
      }
      setEditing({ kind: 'packageMeasure', instanceId })
    }
  }

  /** The chip, or a Review › row, opens one visit's margin popover. */
  function openMargin(visitId: string, at: HTMLElement) {
    anchor.current = at
    setEditing((was) => (was?.kind === 'margin' && was.visitId === visitId
      ? null
      : { kind: 'margin', visitId }))
  }

  /** "Fix them ›" — the first line with no cost, in its own editor (§6.5). */
  function fixCosts(lineId: string) {
    const service = quote?.visits.flatMap((visit) => visit.services)
      .find((one) => one.lines.some((line) => line.id === lineId))
    if (!service) return
    anchor.current = document.querySelector(`[data-line-id="${lineId}"]`)
    setOpenService(service.id)
    setEditing({ kind: 'line', lineId, serviceId: service.id })
  }

  function openLine(lineId: string, serviceId: string) {
    return (event?: unknown) => {
      if (event && typeof event === 'object' && 'currentTarget' in event) {
        at(event as { currentTarget: HTMLElement })
      }
      setOpenService(serviceId)
      setEditing({ kind: 'line', lineId, serviceId })
    }
  }

  /**
   * The + links open the search on their own chip.
   *
   * "+ Deployment" opening on "All" made the link a slower way of pressing
   * ⌘/ — somebody who has already said what they are adding should not have
   * to say it again to the filter.
   */
  /*
   * The picker hangs off the link that asked for it, not off the search box
   * at the top of the page. It used to do the latter, so "+ Deployment" on
   * the fourth service of the third visit opened a panel at the top of the
   * window with nothing to say which row it was about.
   */
  function openAdd(serviceId: string, kind: QuoteLineKind, at: HTMLElement) {
    setOpenService(serviceId)
    anchor.current = at
    setEditing({ kind: 'add', startKind: PICK_CHIP(kind) })
  }

  /**
   * A package onto the quote — the endpoint that makes a visit per visit.
   *
   * `POST /quotes/:id/packages` has always done the right thing and has never
   * had a caller: the only way a "package" reached a quote from the screen
   * was the service picker's `isPackage` flag, which meant "this service is
   * used somewhere in some package's recipe" and went through
   * `POST /visits/:id/services` — one visit, every line on it. That is the
   * bug Aaron reported, and it was never a bug in the expansion.
   *
   * `startsOn` is today. A package's timings are rules rather than dates
   * (`resolveTiming`), so this is the anchor they are measured from, and the
   * group card on the quote is where it is moved.
   */
  async function addPackage(pick: Pickable) {
    await mutate('/packages', {
      method: 'POST',
      body: { ...pick.body, startsOn: new Date().toISOString().slice(0, 10) },
    })
    setEditing(null)
  }

  /**
   * What a search result does.
   *
   * A catalog service becomes a whole service on the quote, recipe and all.
   * Everything else appends a line to the open service — or, when there is
   * none, makes a service named after the thing and puts it inside, in one
   * call, because "add a bag of sand" should not fail with "open a service
   * first" and should not leave an empty service named "Polymeric sand"
   * either.
   */
  /**
   * A catalog service onto a visit. There is no other way to make one.
   * `afterServiceId` is a service ⋮'s **Add a service below** (item 4).
   */
  async function addService(visitId: string, pick: Pickable, afterServiceId?: string) {
    await mutate(`/visits/${visitId}/services`, {
      method: 'POST',
      body: afterServiceId ? { ...pick.body, after: afterServiceId } : pick.body,
    })
    setEditing(null)
  }

  /** `afterLineId` is a line ⋮'s **Add an item below** (item 4): into its service, under it. */
  async function add(pick: Pickable, asNewService: boolean, afterLineId?: string) {
    // Re-read rather than close over the narrowed value: this runs later, and
    // by then the tree is whatever the last mutation returned.
    const tree = quote
    if (!tree) return

    const visitId = tree.visits.find(
      (visit) => visit.services.some((service) => service.id === openService))?.id
      ?? tree.visits[tree.visits.length - 1]?.id

    if (pick.kind === 'service') {
      if (!visitId) {
        await mutate('/visits', { method: 'POST', body: {} })
        setError('Added a visit for it — press ⌘/ again to put the service on it.')
        return
      }
      await mutate(`/visits/${visitId}/services`, { method: 'POST', body: pick.body })
      setEditing(null)
      return
    }

    /* Below a line is in that line's service, whichever one was touched last. */
    const into = (afterLineId ? findLine(tree, afterLineId)?.service.id : undefined) ?? openService
    if (!asNewService && into) {
      await mutate(`/services/${into}/lines`, {
        method: 'POST',
        body: afterLineId ? { ...pick.body, after: afterLineId } : pick.body,
      })
      setEditing(null)
      return
    }

    if (!visitId) {
      setError('Add a visit first — a service belongs to a trip to the property.')
      return
    }
    // A service named after what is going into it, holding it already.
    await mutate(`/visits/${visitId}/services`,
      { method: 'POST', body: { name: pick.label, line: pick.body } })
    setEditing(null)
  }

  /*
   * **One ⋮ menu on the quote and the package** (Board 7): Rename ·
   * Duplicate · Add a visit after this one · ✓ Optional · Move up · Move down
   * · Collapse · Remove. A move that cannot happen is absent rather than dead.
   */
  function visitMenu(visit: QuoteVisitView): MenuItem[] {
    if (!canEdit || !quote) return []
    const order = quote.visits.map((one) => one.id)
    const at = order.indexOf(visit.id)
    const move = (to: number) => {
      const next = [...order]
      next.splice(to, 0, ...next.splice(at, 1))
      void mutate('/reorder', { method: 'POST', body: { visits: next } })
    }
    return [
      {
        key: 'rename', label: VISIT_MENU.rename,
        onSelect: () => { setSelected(visit.id); setEditing({ kind: 'visit', visitId: visit.id, section: 'name' }) },
      },
      { key: 'duplicate', label: VISIT_MENU.duplicate, onSelect: () => void duplicateVisit(visit.id) },
      { key: 'after', label: VISIT_MENU.addAfter, onSelect: () => void addVisitAfter(visit.id) },
      {
        key: 'optional',
        // One item, ticked when on (Board 7) — the word the customer reads.
        label: OPTIONAL_LABEL,
        sub: OPTIONAL_SUB,
        checked: visit.optional,
        onSelect: () => void mutate(`/visits/${visit.id}`,
          { method: 'PATCH', body: { optional: !visit.optional } }),
      },
      ...(at > 0 ? [{ key: 'up', label: VISIT_MENU.moveUp, onSelect: () => move(at - 1) }] : []),
      ...(at < order.length - 1 ? [{ key: 'down', label: VISIT_MENU.moveDown, onSelect: () => move(at + 1) }] : []),
      {
        key: 'collapse',
        label: collapsed.has(visit.id) ? 'Expand' : VISIT_MENU.collapse,
        onSelect: () => toggle(visit.id),
      },
      {
        key: 'delete',
        label: VISIT_MENU.remove,
        tone: 'danger',
        separatorBefore: true,
        onSelect: () => void mutate(`/visits/${visit.id}`, { method: 'DELETE' }),
      },
    ]
  }

  /** Duplicate / Copy of a visit… (§5.1.3): a deep copy directly below, its dated start cleared. */
  async function duplicateVisit(visitId: string) {
    await mutate(`/visits/${visitId}/duplicate`, { method: 'POST' })
  }

  /**
   * Add a visit after this one (§5.1.4): a blank Work visit directly below,
   * starting when this one is done, opened with its Name focused.
   */
  async function addVisitAfter(visitId: string) {
    const ok = await mutate('/visits', { method: 'POST', body: { afterVisitId: visitId, follow: true } })
    const tree = lastTree.current
    if (!ok || !tree) return
    const made = tree.visits.find((one) => one.when.starts.kind === 'after'
      && one.when.starts.anchorId === visitId
      && tree.visits.indexOf(one) === tree.visits.findIndex((v) => v.id === visitId) + 1)
    if (!made) return
    window.requestAnimationFrame(() => {
      anchor.current = document.getElementById(`visit-${made.id}`)
      setSelected(made.id)
      setEditing({ kind: 'visit', visitId: made.id, section: 'name' })
    })
  }

  /** A start from Board 10's Add visit menu. */
  function startVisit(start: 'blank' | 'service' | 'package' | 'other') {
    if (start === 'blank') { void mutate('/visits', { method: 'POST', body: {} }); return }
    /*
     * The service picker — the one a visit's own `+ Add service` opens — hung
     * off the Add visit button rather than the search box. It used to open
     * the line picker here, so "From a service…" offered materials and fees
     * and dropped the pick into whichever visit was open (Aaron, item 3.2).
     * On a phone the toolbar is not drawn and `addVisitBox` is empty, so the
     * anchor stays the button the sheet was opened from.
     */
    if (start === 'service') {
      anchor.current = addVisitBox.current ?? anchor.current
      setEditing({ kind: 'addServiceVisit' })
      return
    }
    if (start === 'package') { anchor.current = packageBox.current; setEditing({ kind: 'addPackage' }) }
  }

  /**
   * A catalog service on a visit of its own: the visit first, then the
   * service on it.
   *
   * Two requests because `POST /visits` takes no service. The new visit is
   * found as the one id the answer has that the tree before it did not —
   * not as "the last visit", which is a fact about sort order rather than
   * about which row this call made. It is left unnamed on purpose: an empty
   * name reads as its services (`visitName()`), so a visit holding one
   * service is called what the service is called, exactly as a blank visit
   * is once something is put on it.
   */
  async function addServiceVisit(pick: Pickable) {
    const before = new Set(quote?.visits.map((one) => one.id) ?? [])
    const ok = await mutate('/visits', { method: 'POST', body: {} })
    const made = ok ? lastTree.current?.visits.find((one) => !before.has(one.id)) : undefined
    if (made) {
      await mutate(`/visits/${made.id}/services`, { method: 'POST', body: pick.body })
      setSelected(made.id)
    }
    // Closed either way, as `addService` does: a refusal is on the page in `error`.
    setEditing(null)
  }

  /*
   * **The service's ⋮** (Aaron, review item 4): the visit's menu one level
   * down, in its order and with its words — Rename · Duplicate · Add a
   * service below · ✓ Optional · Move up · Move down · Collapse · Remove
   * service. A move is within the visit, and one that cannot happen is
   * absent, as on a visit. `from` is the ⋮ itself, so what Rename and Add
   * below open hangs off the row they were asked for; a phone draws them as
   * sheets and needs none. Collapse is left off a phone, where a service is
   * a screen of its own rather than a fold in a card.
   */
  function serviceMenu(serviceId: string, from?: HTMLElement): MenuItem[] {
    if (!canEdit || !quote) return []
    const visit = quote.visits.find((one) => one.services.some((service) => service.id === serviceId))
    const service = visit?.services.find((one) => one.id === serviceId)
    if (!visit || !service) return []
    const order = visit.services.map((one) => one.id)
    const at = order.indexOf(serviceId)
    const move = (to: number) => {
      const next = [...order]
      next.splice(to, 0, ...next.splice(at, 1))
      void mutate('/reorder', { method: 'POST', body: { services: { [visit.id]: next } } })
    }
    const hang = () => { if (from) anchor.current = from }
    return [
      {
        key: 'rename', label: VISIT_MENU.rename,
        onSelect: () => {
          hang()
          setOpenService(serviceId)
          setEditing({ kind: 'service', serviceId, focus: 'name' })
        },
      },
      {
        key: 'duplicate', label: VISIT_MENU.duplicate,
        onSelect: () => void mutate(`/services/${serviceId}/duplicate`, { method: 'POST' }),
      },
      {
        key: 'below', label: ROW_MENU.addServiceBelow,
        onSelect: () => {
          hang()
          setEditing({ kind: 'addService', visitId: visit.id, afterServiceId: serviceId })
        },
      },
      {
        key: 'optional', label: OPTIONAL_LABEL, sub: OPTIONAL_SUB, checked: service.optional,
        onSelect: () => void mutate(`/services/${serviceId}`,
          { method: 'PATCH', body: { optional: !service.optional } }),
      },
      ...(at > 0 ? [{ key: 'up', label: VISIT_MENU.moveUp, onSelect: () => move(at - 1) }] : []),
      ...(at < order.length - 1
        ? [{ key: 'down', label: VISIT_MENU.moveDown, onSelect: () => move(at + 1) }] : []),
      ...(phone ? [] : [{
        key: 'collapse',
        label: collapsed.has(serviceId) ? ROW_MENU.expand : VISIT_MENU.collapse,
        onSelect: () => toggle(serviceId),
      }]),
      {
        key: 'delete',
        label: ROW_MENU.removeService,
        tone: 'danger',
        separatorBefore: true,
        onSelect: () => void mutate(`/services/${serviceId}`, { method: 'DELETE' }),
      },
    ]
  }

  /*
   * **The line's ⋮** (item 4): the service's menu for one line — Duplicate ·
   * Add an item below · ✓ Optional · Move up · Move down · Reset to standard
   * price · Remove line. No Rename: a line's name is the catalog row it
   * names, and its popover has no Name to open on. No Collapse: a line is
   * one row. Reset, offered only where a price was typed over, takes the
   * place Collapse has one level up. Add an item below opens the picker on
   * the line's own kind, because the next thing under a material is most
   * often another material.
   */
  function lineMenu(lineId: string, edited: boolean, from?: HTMLElement): MenuItem[] {
    if (!canEdit || !quote) return []
    const found = findLine(quote, lineId)
    if (!found) return []
    const { line, service } = found
    const order = service.lines.map((one) => one.id)
    const at = order.indexOf(lineId)
    const move = (to: number) => {
      const next = [...order]
      next.splice(to, 0, ...next.splice(at, 1))
      void mutate('/reorder', { method: 'POST', body: { lines: { [service.id]: next } } })
    }
    return [
      {
        key: 'duplicate', label: VISIT_MENU.duplicate,
        onSelect: () => void mutate(`/lines/${lineId}/duplicate`, { method: 'POST' }),
      },
      {
        key: 'below', label: ROW_MENU.addItemBelow,
        onSelect: () => {
          anchor.current = from ?? document.querySelector(`[data-line-id="${lineId}"]`)
          setOpenService(service.id)
          setEditing({ kind: 'add', startKind: PICK_CHIP(line.kind), afterLineId: lineId })
        },
      },
      {
        key: 'optional', label: OPTIONAL_LABEL, sub: OPTIONAL_SUB, checked: line.optional,
        onSelect: () => void mutate(`/lines/${lineId}`,
          { method: 'PATCH', body: { optional: !line.optional } }),
      },
      ...(at > 0 ? [{ key: 'up', label: VISIT_MENU.moveUp, onSelect: () => move(at - 1) }] : []),
      ...(at < order.length - 1
        ? [{ key: 'down', label: VISIT_MENU.moveDown, onSelect: () => move(at + 1) }] : []),
      ...(edited
        ? [{
          key: 'reset', label: ROW_MENU.resetPrice,
          onSelect: () => void mutate(`/lines/${lineId}/reset`, { method: 'POST' }),
        }]
        : []),
      {
        key: 'delete',
        label: ROW_MENU.removeLine,
        tone: 'danger',
        separatorBefore: true,
        onSelect: () => void mutate(`/lines/${lineId}`, { method: 'DELETE' }),
      },
    ]
  }
}

/** A line and the service holding it — the editor needs both. */
export function findLine(quote: QuoteTree, lineId: string) {
  for (const visit of quote.visits) {
    for (const service of visit.services) {
      const line = service.lines.find((candidate) => candidate.id === lineId)
      if (line) return { line, service }
    }
  }
  return null
}

/**
 * The toast after Send: who the proposal went to, or why it went to nobody —
 * white, as every state change's toast is (walkthrough findings §11, Oct 30).
 */
export function sendNote(
  emailedTo: { name: string; email: string }[], notEmailed: string | null,
): { tone: 'default'; text: string } {
  if (emailedTo.length === 0) return { tone: 'default', text: `Sent, but not emailed. ${notEmailed ?? ''}`.trim() }
  const names = emailedTo.map((person) => `${person.name} (${person.email})`)
  const list = names.length === 1 ? names[0]! : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]!}`
  return { tone: 'default', text: `Proposal emailed to ${list}.` }
}

export function findService(quote: QuoteTree, serviceId: string) {
  for (const visit of quote.visits) {
    const service = visit.services.find((candidate) => candidate.id === serviceId)
    if (service) return service
  }
  return null
}
